import Chip from '@mui/material/Chip';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import * as d3 from 'd3';
import {useEffect} from 'react';
import Formatter from '../../util/formatter';

const useStyles = makeStyles((theme) => ({
    root: {
        minWidth: 50,
        '&.green': {
            backgroundColor: '#4EAC6F',
            color: '#FFFFFF',
        },
        '&.amber': {
            backgroundColor: '#F7A202',
            color: '#FFFFFF',
        },
        '&.red': {
            backgroundColor: '#E25B61',
            color: '#FFFFFF',
        },
        '&.grey': {
            backgroundColor: '#DEE1E5',
            color: '#222222',
        },
        '&.blue': {
            backgroundColor: '#0864D8',
            color: '#FFFFFF',
        },
    },
}));

const OngoingSpellProgress = (props) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const emptyFunction = function () {
        return undefined;
    };

    const id = Formatter.generateRandomString(10);

    return (
        <Chip
            size={props.size}
            label={t(props.label)}
            onDelete={emptyFunction}
            deleteIcon={
                <LoadingDots
                    id={id}
                    color={props.color}
                    size={props.size}
                    animated={props.animated}
                />
            }
            className={clsx(classes.root, props.color)}
        />
    );
};

const COLOR_OPTIONS = {
    blue: '#EFF1F3',
    grey: '#BFC2C7',
    green: '#EFF1F3',
    amber: '#EFF1F3',
    red: '#EFF1F3',
};

const SIZE_OPTIONS = {
    small: {
        dotRadius: 3,
        spacing: 10,
        svgWidth: 40,
        svgHeight: 10,
    },
    medium: {
        dotRadius: 5,
        spacing: 15,
        svgWidth: 50,
        svgHeight: 20,
    },
};

const LoadingDots = ({id, size, color, animated}) => {
    const svgId = `loading-dots-${id}`;

    useEffect(() => {
        const {dotRadius, spacing, svgWidth, svgHeight} = SIZE_OPTIONS[size];

        const svg = d3.select(`#${svgId}`)
            .attr('width', svgWidth)
            .attr('height', svgHeight);

        const dots = svg.selectAll('circle')
            .data([0, 1, 2])
            .enter()
            .append('circle')
            .attr('cx', (d, i) => i * spacing + dotRadius)
            .attr('cy', svgHeight / 2)
            .attr('r', dotRadius)
            .style('fill', COLOR_OPTIONS[color])
            .style('opacity', animated ? 0.3 : 1);

        const animateDots = () => {
            dots.transition()
                .duration(300)
                .delay((d, i) => i * 150)
                .style('opacity', 1)
                .transition()
                .duration(300)
                .style('opacity', 0.3)
                .on('end', animateDots);
        };

        if (animated) animateDots();
    }, [size, color, svgId]);
    return (
        <svg id={svgId}/>
    );
};

export default OngoingSpellProgress;

OngoingSpellProgress.propTypes = {
    label: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    color: PropTypes.oneOf(['blue', 'amber', 'green', 'red', 'grey']),
    animated: PropTypes.bool,
};

OngoingSpellProgress.defaultProps = {
    label: 'Active',
    size: 'medium',
    color: 'green',
    animated: false,
};